import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Zap, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

// Use relative URL so requests go through the server.js /api proxy regardless of environment
const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '') ? process.env.REACT_APP_BACKEND_URL : '';

// The OpenSail auto-login endpoint base URL.
const AUTOLOGIN_URL = process.env.REACT_APP_OPENSAIL_AUTOLOGIN_URL || '/opensail/auto-login';

const EMERGE_TOKEN_KEY = 'emerge_token_backup';

const SnagcartPage = () => {
  const navigate = useNavigate();
  const [iframeUrl, setIframeUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const savedTokenRef = useRef(null);
  const iframeRef = useRef(null);

  const initIframe = (token) => {
    fetch(`${API_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to get user info');
        return res.json();
      })
      .then(user => {
        const ts = Date.now();
        const email = encodeURIComponent(user.email);
        const name = encodeURIComponent(user.name || user.email);
        const url = `${AUTOLOGIN_URL}?email=${email}&name=${name}&t=${ts}`;
        setIframeUrl(url);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error setting up iframe:', err);
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    let token = localStorage.getItem('token');
    const existingBackup = localStorage.getItem(EMERGE_TOKEN_KEY);
    if (existingBackup) {
      token = existingBackup;
      localStorage.setItem('token', token);
    }

    if (!token) {
      navigate('/auth');
      return;
    }

    savedTokenRef.current = token;
    localStorage.setItem(EMERGE_TOKEN_KEY, token);
    initIframe(token);

    return () => {
      const backup = savedTokenRef.current || localStorage.getItem(EMERGE_TOKEN_KEY);
      if (backup) {
        localStorage.setItem('token', backup);
        localStorage.removeItem(EMERGE_TOKEN_KEY);
      }
    };
  }, [navigate]);

  const openInNewTab = () => {
    if (iframeUrl) {
      window.open(iframeUrl, '_blank');
    }
  };

  const handleReload = () => {
    setIframeLoaded(false);
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col" data-testid="snagcart-page">
      {/* Header */}
      <div className="sticky top-0 left-0 right-0 h-12 bg-[#111111] border-b border-[#222222] z-50 flex items-center px-3 flex-shrink-0 gap-2">
        {/* Back button */}
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          size="sm"
          className="text-zinc-500 hover:text-white h-7 w-7 p-0 rounded-md"
          title="Back to Dashboard"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </Button>

        {/* Divider */}
        <div className="w-px h-4 bg-[#333333]" />

        {/* Brand */}
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md" style={{background: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)'}}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 3.5h10M2 7h6.5M2 10.5h5" stroke="white" strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="11" cy="9.5" r="2.5" fill="white" fillOpacity="0.9"/>
              <path d="M10 9.5l.8.8L12.5 8.5" stroke="#f97316" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="text-sm font-semibold text-white">Snagcart AI Builder</span>
        </div>

        {/* Status indicator */}
        {iframeLoaded && (
          <div className="flex items-center gap-1.5 ml-1">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[11px] text-zinc-500">Live</span>
          </div>
        )}

        {/* Right actions */}
        <div className="ml-auto flex items-center gap-1">
          {iframeUrl && (
            <>
              <Button
                onClick={handleReload}
                variant="ghost"
                size="sm"
                className="text-zinc-500 hover:text-white h-7 w-7 p-0 rounded-md"
                title="Reload"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
              <Button
                onClick={openInNewTab}
                variant="ghost"
                size="sm"
                className="text-zinc-500 hover:text-white h-7 w-7 p-0 rounded-md"
                title="Open in new tab"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col" style={{ height: 'calc(100vh - 3rem)' }}>
        {loading && (
          <div className="flex-1 flex items-center justify-center bg-[#0a0a0a]">
            <div className="text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl mx-auto mb-4" style={{background: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)'}}>
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div className="w-6 h-6 border-2 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-zinc-400 text-sm font-medium">Loading Snagcart AI Builder...</p>
              <p className="text-zinc-600 text-xs mt-1">Setting up your workspace</p>
            </div>
          </div>
        )}
        {error && (
          <div className="flex-1 flex items-center justify-center bg-[#0a0a0a]">
            <div className="text-center max-w-sm">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-red-400 text-xl">!</span>
              </div>
              <p className="text-red-400 font-medium mb-1">Failed to load AI Builder</p>
              <p className="text-zinc-500 text-sm mb-4">{error}</p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-sm"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}
        {iframeUrl && !loading && (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            title="Snagcart AI Builder"
            className="w-full flex-1 border-0"
            style={{ height: '100%' }}
            allow="cross-origin-isolated"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
            onLoad={() => setIframeLoaded(true)}
          />
        )}
      </div>
    </div>
  );
};

export default SnagcartPage;
